import React from 'react'
import { useSelector } from 'react-redux'
import Link from 'redux-first-router-link'

import ViewPublishedPost from './ViewPublishedPost'
import styles from './ViewPublishedPost.css'

import RelativeTime from 'components/RelativeTime'
import ReadTime from 'components/ReadTime'
import Badge from 'components/Badge'
import ViewPostPageExtraNav from './ViewPostPageExtraNav'

import ErrorPanel from 'components/ErrorPanel'
import LoadingPlaceholder from 'components/LoadingPlaceholder'
import { ReactFC } from 'types'
import { Post } from 'posts/posts.types'
import { User } from 'profile/profile.types'
import { EventData } from "posts/EventData"
import styled, { createGlobalStyle } from 'styled-components'

import { AccessDenied } from "components/AccessDenied"
import { useCircle } from 'circle/circle.selectors'
import { Link as MuiLink, } from "@material-ui/core"

import { Link as RouterLink } from "react-router-dom"
import { circleUrl } from 'circle/places'
import { PostPageVisitor } from 'queues/PostPageVisitor'
import { PostViewDataContextProvider } from 'posts/PostViewDataContext'
import { BodyScrollLayout } from 'navigation-ui/BodyScrollLayout'
import { PostContextProvider } from 'posts/PostContext'
import { AccessContextProvider } from 'posts/AccessContext'
import {
  PostViewPage
} from "lib-core"

const Meta: ReactFC<{ post: Post, profile: User }> = ({ post, profile }) => {
  if (!post) {
    return null
  }
  const { author } = post
  const posts =
    profile && profile._id === author._id
      ? { type: 'profile/public/posts' }
      : {
        type: 'home/member/posts',
        payload: { username: author.username }
      }
  return (
    <div className={styles.meta_holder} data-intro="profile">
      <Link to={posts}>
        <PostUser post={post} />
      </Link>
    </div>
  )
}

const PostUser: ReactFC<{ post: Post }> = ({ post }) => {
  if (post.event) {
    return <EventUser post={post} />
  }
  return <ArticleUser post={post} />
}

const EventUser: ReactFC<{ post: Post }> = ({ post }) => {
  const { created, readTime, author } = post
  return (
    <Badge user={author} linkToProfile={false}>
    </Badge>
  )
}

const ArticleUser: ReactFC<{ post: Post }> = ({ post }) => {
  const { created, readTime, author } = post
  return (
    <Badge user={author} linkToProfile={false}>
      <span className={styles.meta_time}>
        <RelativeTime value={created} /> &middot;
        <ReadTime value={readTime} />
      </span>
    </Badge>
  )
}

const EventTitleWrapper = styled.div`
  display: flex;
  margin: 0 20px;
  width: 100%;
`


const EventTitle: ReactFC<{ post: Post }> = ({ post }) => {
  const event = new EventData(post)
  const startTime = event.localStartTime

  return (
    <EventTitleWrapper>
      <RelativeTime value={startTime} />
    </EventTitleWrapper>
  )
}


const PostTitle: ReactFC<{ post: Post }> = ({ post }) => {
  if (!post || !post.event) {
    return null
  }

  return (
    <EventTitle post={post}></EventTitle>
  )
}

const CircleInfoContainer = styled.div`
  align-self: center;
  text-align: center;
  margin: 2em;
  color: #4D4D4D;
`

const CircleInfo: ReactFC = () => {
  const circle = useCircle()
  if (!circle) {
    return null
  }

  return (
    <CircleInfoContainer>
      <MuiLink component={RouterLink} to={circleUrl(circle._id)} underline="always">
        {circle.name}
      </MuiLink>
    </CircleInfoContainer>
  )
}

const ViewPublishedPageContent: ReactFC = () => {
  const {
    loading,
    post,
    error,
    profile,
  } = useSelector(selectForPublishedPostPage)

  if (profile && error?.message === "access_denied") {
    return (
      <AccessDenied>
        <CircleInfo />
      </AccessDenied>
    )
  }

  if (!post && error) {
    return <ErrorPanel error={error} />
  }

  if (!post && loading) {
    return <LoadingPlaceholder />
  }

  return (
    <ViewPublishedPost post={post} />
  )
}

const ViewPublishedPostPage1 = () => {
  const {
    post,
    profile,
    domainName
  } = useSelector(selectForPublishedPostPage)

  return (
    <PostViewDataContextProvider>
      <PostContextProvider post={post}>
        <AccessContextProvider>
          <BodyScrollLayout
            left={<Meta post={post} profile={profile} />}
            center={<PostTitle post={post} />}
            right={<ViewPostPageExtraNav post={post} domainName={domainName} />}
          >
            <PostPageVisitor>
              <ViewPublishedPageContent />
            </PostPageVisitor>
          </BodyScrollLayout>
        </AccessContextProvider>
      </PostContextProvider>
    </PostViewDataContextProvider>
  )
}

const selectForPublishedPostPage = (state) => {
  const {
    location: { payload },
    post: { error, value: post },
    session: { value: profile },
    // instance: { value: { domainName } }
  } = state

  let loading = false
  if (payload.post) {
    loading = !post || post._id !== payload.post
  } else {
    loading = !post || (post.name !== payload.name || post.author?.username !== payload.username)
  }
  return {
    postId: (post && post._id) || payload.post,
    loading,
    post,
    error,
    profile,
    // domainName
  }
}

const CssOverrides = createGlobalStyle`
  #body #AbsoluteFlexColumn {
    position: static;
  }
`

const ViewPublishedPostPage = () => {
  const {
    postId
  } = useSelector(selectForPublishedPostPage)

  return (
    <>
      <CssOverrides />
      <PostViewPage postId={postId} />
    </>
  )
}

export default ViewPublishedPostPage
